import { useCallback } from 'react';

import { create, show, useModal } from '@ebay/nice-modal-react';
import { useTranslation } from 'react-i18next';
import { competitionEngine } from 'tods-competition-factory';

import { ChoosePlayerDialog } from 'components/dialogs/playerProfileDialog/ChoosePlayerDialog';
import { type ParticipantInterface } from 'typedefs/store/tmxTypes';

type PartialParticipant = Partial<ParticipantInterface> & Required<Pick<ParticipantInterface, 'participantId'>>;
export type ParticipantOrId = PartialParticipant | string;
interface ModalProps {
  participants: PartialParticipant[];
}

const Modal = create(({ participants }: ModalProps) => {
  const modal = useModal();

  const handleClose = useCallback(() => {
    modal.hide();
  }, [modal]);

  const handleSelect = useCallback(
    (e, participant) => {
      modal.resolve(participant);
      modal.hide();
    },
    [modal],
  );

  return (
    <ChoosePlayerDialog
      isOpen={modal.visible}
      participants={participants}
      onClose={handleClose}
      onExited={modal.remove}
      onSelect={handleSelect}
    />
  );
});

/**
 * Open a player profile dialog with chosen participant(s)
 * Providing ParticipantInterface is suggested to prevent looking up participant names
 */
export const usePlayerSelect = (): ((
  participants: ParticipantOrId[],
  includeIndividualParticipants?: boolean,
) => Promise<PartialParticipant>) => {
  const { t } = useTranslation();
  return useCallback(
    async (participants, includeIndividualParticipants = true) => {
      const hydratedParticipants = participants
        .map<string>((participant) => (typeof participant === 'string' ? participant : participant.participantId))
        .flatMap<ParticipantInterface>(
          (participantIds) =>
            competitionEngine.getCompetitionParticipants({ participantFilters: { participantIds } })?.participants ??
            [],
        )
        .flatMap<ParticipantInterface>((participant) =>
          includeIndividualParticipants && !!participant?.individualParticipantIds?.length
            ? competitionEngine.getCompetitionParticipants({
                participantFilters: {
                  participantIds: participant.individualParticipantIds,
                },
              })?.participants ?? []
            : [participant],
        );

      if (!hydratedParticipants.length) throw new Error(t('noParticipantsProvided'));

      if (hydratedParticipants.length === 1) return hydratedParticipants[0];

      return show(Modal, {
        participants: hydratedParticipants,
      });
    },
    [t],
  );
};
